import type { ListJobsResponse } from '@vy/api-definition';
import type { JobTaskType, JobType } from '@vy/jobs-types';
import { JOB_STATUSES, JOB_TASK_TYPE_LABELS, JOB_TYPES } from '@vy/jobs-types';
import { isNotEmptyString } from '@vy/utils';
import type { NotificationCardProps, NotificationStatus } from './VyNotificationCard';
import { raise } from '@vy/err';
import { defined } from '@vy/null';
import { ViewReportNotImplementedError } from '../feedback-errors';
import { formatRelativeTime } from '../../../../utils/format-relative-time';

type Job = ListJobsResponse[number];
type JobTask = Job['tasks'][number];

const INVOICE_JOB_TYPES = new Set<JobType>([
  JOB_TYPES.NetSuiteExportInvoice,
  JOB_TYPES.NetSuiteExportInvoiceSalesOrder,
  JOB_TYPES.XeroExportInvoice,
  JOB_TYPES.StripeExportInvoice,
  JOB_TYPES.ExportInvoice,
  JOB_TYPES.UpdateInvoiceBillingStatus,
]);

const SALES_ORDER_JOB_TYPES = new Set<JobType>([JOB_TYPES.NetSuiteExportSalesOrder]);

const CUSTOMER_JOB_TYPES = new Set<JobType>([JOB_TYPES.LinkNetSuiteCustomer, JOB_TYPES.LinkMorningCustomer]);

const EMAIL_JOB_TYPES = new Set<JobType>([JOB_TYPES.SendInvoiceEmail]);

export const WEBHOOK_JOB_TYPES = new Set<JobType>([
  JOB_TYPES.StripeWebhookCustomerCreated,
  JOB_TYPES.StripeWebhookInvoiceUpdated,
  JOB_TYPES.StripeWebhookPaymentIntent,
]);

interface TaskDisplayContext {
  invoiceNumber: string | undefined;
  customerName: string | undefined;
  contractName: string | undefined;
  recipient: string | undefined;
}

function extractTaskDisplayContext(task: JobTask): TaskDisplayContext {
  const metadata = task.metadata as Record<string, unknown>;

  return {
    invoiceNumber: metadata.invoiceNumber as string | undefined,
    customerName: metadata.customerName as string | undefined,
    contractName: metadata.contractName as string | undefined,
    recipient: metadata.recipient as string | undefined,
  };
}

export function buildNotificationTitle(job: Job): string {
  const baseLabel = formatJobType(job.type);

  if (job.totalTasks === 0) {
    return baseLabel;
  }

  if (WEBHOOK_JOB_TYPES.has(job.type)) {
    const firstTaskMetadata = job.tasks[0].metadata as Record<string, unknown>;
    const cardTitle = firstTaskMetadata.cardTitle;
    if (typeof cardTitle === 'string' && isNotEmptyString(cardTitle)) {
      return cardTitle;
    }
    return baseLabel;
  }

  if (job.totalTasks > 1) {
    return `${baseLabel} (${job.totalTasks} tasks)`;
  }

  const context = extractTaskDisplayContext(job.tasks[0]);

  if (INVOICE_JOB_TYPES.has(job.type)) {
    if (context.invoiceNumber && context.customerName) {
      return `${baseLabel} ${context.invoiceNumber} for ${context.customerName}`;
    }

    if (context.invoiceNumber) {
      return `${baseLabel} ${context.invoiceNumber}`;
    }

    if (context.customerName) {
      return `${baseLabel} for ${context.customerName}`;
    }

    return baseLabel;
  }

  if (SALES_ORDER_JOB_TYPES.has(job.type)) {
    if (context.contractName && context.customerName) {
      return `${baseLabel} ${context.contractName} for ${context.customerName}`;
    }

    if (context.contractName) {
      return `${baseLabel} ${context.contractName}`;
    }

    if (context.customerName) {
      return `${baseLabel} for ${context.customerName}`;
    }

    return baseLabel;
  }

  if (CUSTOMER_JOB_TYPES.has(job.type)) {
    if (context.customerName) {
      return `${baseLabel} ${context.customerName}`;
    }

    return baseLabel;
  }

  if (EMAIL_JOB_TYPES.has(job.type)) {
    if (context.recipient) {
      return `${baseLabel} to ${context.recipient}`;
    }

    return baseLabel;
  }

  return baseLabel;
}

const JOB_TYPE_DISPLAY_OVERRIDES: Record<string, string> = {
  ExtractContractFromPdf: 'Extract contract from PDF',
  NetSuiteExportInvoiceSalesOrder: 'Sync invoice as Sales Order',
  NetSuiteExportSalesOrder: 'NetSuite export Sales Order',
};

export function mapJobToNotification(job: Job): NotificationCardProps {
  const handleViewReport = () => {
    raise(new ViewReportNotImplementedError());
  };

  return {
    id: job.id,
    title: buildNotificationTitle(job),
    metadata: getJobMetadata(job),
    triggeredAt: formatRelativeTime(new Date(job.createdAt)),
    status: getNotificationStatus(job.status),
    readAt: job.notification?.readAt ?? null,
    onViewReport: handleViewReport,
  };
}

export function formatTaskType(type: JobTaskType): string {
  return JOB_TASK_TYPE_LABELS[type];
}

export function formatJobType(type: string): string {
  const override = JOB_TYPE_DISPLAY_OVERRIDES[type];

  if (defined(override)) {
    return override;
  }

  const withSpaces = type.replace(/([A-Z])/g, ' $1').trim();
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).toLowerCase();
}

function getJobMetadata(job: Job): string {
  switch (job.status) {
    case JOB_STATUSES.InProgress:
    case JOB_STATUSES.Pending:
      return `${job.completedTasks} / ${job.totalTasks}`;
    case JOB_STATUSES.Completed:
      return `${job.totalTasks} / ${job.totalTasks}`;
    case JOB_STATUSES.Failed:
    case JOB_STATUSES.PartiallyCompleted:
      return `${job.errorCount} failed`;
    default:
      return '';
  }
}

function getNotificationStatus(jobStatus: Job['status']): NotificationStatus {
  switch (jobStatus) {
    case JOB_STATUSES.Pending:
    case JOB_STATUSES.InProgress:
      return 'syncing';
    case JOB_STATUSES.Completed:
      return 'done';
    case JOB_STATUSES.Failed:
    case JOB_STATUSES.PartiallyCompleted:
      return 'error';
    default:
      return 'syncing';
  }
}
